import React, { useState, useContext, useEffect } from 'react';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import AppContext from '../Hooks/AppContext';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

const IdentityCardType = props => {
  /**
   * Constants
   */

  /**
   * States
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [defaultData, setDefaultData] = useState(null);
  const [data, setData] = useState(null);
  const [notification, setNotification] = useState(null);

  /**
   * Hooks
   */
  const ac = useContext(AppContext);

  /**
   * Effects
   */
  useEffect(() => {
    ac.ajax('getIdentityCardType', {
      idIdentityCardType: props.value.id,
    })
      .then(response => {
        if (response.status.ok) {
          if (Boolean(response.result.data.identityCardType.id)) {
            setDefaultData(response.result.data.identityCardType);
            setData(response.result.data.identityCardType);
          } else {
            setDefaultData(response.result.defaultValues.identityCardType);
            setData({
              ...response.result.data.identityCardType,
              identityCardType: props.value.identityCardType,
            });
          }

          // setDeps(response.result.dependencies);

          setIsLoaded(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    let message = null;

    message = message !== null ? message : data.identityCardType === '' ? 'Nu ati completat denumirea actului de identitate' : message;
    message = message !== null ? message : data.identityCardTypeShort === '' ? 'Nu ati completat prescurtarea denumirii actului de identitate' : message;

    message = message !== null ? message : JSON.stringify(data) === JSON.stringify(defaultData) ? 'Nu ati efectuat nici o modificare' : null;

    setNotification(message);
  }, [isLoaded, defaultData, data]);

  /**
   * Getters
   */

  /**
   * Handlers
   */
  const handleDataChange = () => evt => {
    let mods = {};
    switch (evt.target.name) {
      default: break;
    }

    switch (evt.target.type) {
      case 'checkbox':
        setData(prevState => ({
          ...prevState,
          [evt.target.name]: evt.target.checked,
          ...mods,
        }));
        break;
      case 'component':
        setData(prevState => ({
          ...prevState,
          ...evt.target.data,
          ...mods,
        }));
        break;
      default:
        setData(prevState => ({
          ...prevState,
          [evt.target.name]: evt.target.value,
          ...mods,
        }));
    }
  };

  const handleClose = (evt, reason) => {
    switch (reason) {
      case 'backdropClick':
        break;
      case 'escapeKeyDown':
        if (JSON.stringify(data) === JSON.stringify(defaultData)) {
          handleCancel()();
        }
        break;
      default: break;
    }
  };

  const handleCancel = () => () => props.onCancel();
  const handleConfirm = () => () => {
    ac.ajax('saveIdentityCardType', data)
      .then(response => {
        if (response.status.ok) {
          props.onConfirm(response);
        }
      });
  };

  /**
   * Renderer
   */
  if (!isLoaded) {
    return (
      <Backdrop open sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Dialog maxWidth='sm' open keepMounted scroll='paper'
      disableEscapeKeyDown={ac.isLoading}
      onClose={handleClose}
    >
      {/**
       * Title
      */}
      <DialogTitle>Tip act de identitate: {defaultData.id === '' ? 'Adaugare' : 'Modificare'}</DialogTitle>

      {/**
       * Content
      */}
      <DialogContent>
        <DialogContentText>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <TextField disabled
                label='ID'
                placeholder='ID'
                name='id'
                value={data.id}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={7}>
              <TextField required autoFocus
                disabled={ac.isLoading}
                label='Tip act de identitate'
                placeholder='Tip act de identitate'
                name='identityCardType'
                value={data.identityCardType}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField required
                disabled={ac.isLoading}
                label='Prescurtare'
                placeholder='Prescurtare'
                name='identityCardTypeShort'
                value={data.identityCardTypeShort}
                onChange={handleDataChange()}
              />
            </Grid>
            <Grid item xs={12} sx={{justifyContent: 'space-between'}}>
              <FormControlLabel
                label='Serie'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='hasSerial'
                    checked={data.hasSerial}
                    onChange={handleDataChange()}
                  />
                }
              />
              <FormControlLabel
                label='Numar'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='hasNumber'
                    checked={data.hasNumber}
                    onChange={handleDataChange()}
                  />
                }
              />
              <FormControlLabel
                label='Eliberat de'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='hasIssuedBy'
                    checked={data.hasIssuedBy}
                    onChange={handleDataChange()}
                  />
                }
              />
              <FormControlLabel
                label='Eliberat la'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='hasIssuedAt'
                    checked={data.hasIssuedAt}
                    onChange={handleDataChange()}
                  />
                }
              />
              <FormControlLabel
                label='Valid pana la'
                control={
                  <Checkbox size='small'
                    disabled={ac.isLoading}
                    name='hasValidTo'
                    checked={data.hasValidTo}
                    onChange={handleDataChange()}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField multiline rows={3}
                disabled={ac.isLoading}
                label='Observatii'
                placeholder='Observatii'
                name='description'
                value={data.description}
                onChange={handleDataChange()}
              />
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>

      <DialogContent>
        <DialogContentText color='error' sx={{visibility: Boolean(notification) ? 'initial' : 'hidden'}}>
          {Boolean(notification) ? notification : 'Totul pare OK!'}
        </DialogContentText>
      </DialogContent>

      {/**
       * Actions
      */}
      <DialogActions>
        <Button disabled={ac.isLoading || Boolean(notification)} onClick={handleConfirm()}>Salveaza</Button>
        <Button variant='text' color='inherit' disabled={ac.isLoading} onClick={handleCancel()}>Renunta</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdentityCardType;